/**
 * Single source of truth for how the outreach flow is worded to outreachers.
 *
 *   Found → Messaged → Replied → Joined → Posted
 *
 * - Messaged ("I messaged them"): a DM was sent. Not a log, nothing is owed.
 * - Replied ("Log reply"): the creator replied asking how to join. This is the
 *   log: an invite with a proof screenshot, and the only stage a log bonus can
 *   apply to.
 * - Joined / Posted: confirmed by clipping, or by an admin on teams with
 *   manual tracking.
 *
 * Anything that states a rate rule or a screenshot rule imports from here, so
 * a change lands once. Stage labels and status codes live in `labels.ts`.
 *
 * ── What is actually true about pay ──────────────────────────────────────────
 * Marking someone messaged NEVER earns anything. Logging a reply creates the
 * invite, which is the thing that can earn, but how much and at which stage
 * is the rate card's business. Some teams run a $0 log bonus and pay only on
 * joins and posts. So nothing here may promise "you get paid when they
 * reply". Where a surface needs amounts it derives them from live rates via
 * {@link earningsLine}.
 */
import type { InviteChannel, InviteStatus } from '@/api/types';
import { formatDate, daysSince } from '@/lib/dates';
import { inviteStage, payoutStatusLabel, prospectStatusLabel } from '@/lib/labels';
import { parseSocialHandle, type SocialHandle } from '@/lib/socialHandle';

// ── The two actions ───────────────────────────────────────────────────────

/**
 * The two actions, stated the same way everywhere. This is the distinction
 * outreachers were getting wrong: messaging and logging are different
 * actions with different bars.
 */
const MESSAGED_STEP = {
  verb: 'I messaged them',
  undo: 'Undo',
  done: 'Messaged',
  when: 'You sent the DM',
  means: 'Records that you messaged them so your team can see. Not a log, and nothing is owed for it.',
} as const;

export const STEPS = {
  messaged: MESSAGED_STEP,
  /** @deprecated Old key, kept so existing imports keep working. Same as `messaged`. */
  contacted: MESSAGED_STEP,
  logged: {
    verb: 'Log reply',
    when: 'They replied asking how to join',
    means: 'Creates your invite for this creator, with proof. This is the step that can earn.',
  },
} as const;

/** One line, for tight spaces. Deliberately says nothing about amounts. */
export const TWO_STEP_SHORT =
  'Tap "I messaged them" when you DM someone. Use Log reply only once they answer asking how to join.';

/**
 * Longer explanation for a callout. Still says nothing about amounts: the
 * Rates page is the only surface that quotes numbers.
 */
export const TWO_STEP_LONG =
  'Messaging is unpaid and unlimited. DM as many creators as you like and tap "I messaged them" so your team can see. Logging is a separate step: use Log reply once a creator has replied AND asked how to join. Someone asking only what it pays has not asked to join. If several people messaged the same creator, whoever logs a qualifying reply first gets the invite.';

/** Why bother marking messaged when it pays nothing. */
export const WHY_MARK_MESSAGED =
  "Marking a creator messaged doesn't reserve them and doesn't pay anything. It tells your team you've already messaged them, so they can decide whether to send their own.";
/** @deprecated Old name for {@link WHY_MARK_MESSAGED}. */
export const WHY_MARK_CONTACTED = WHY_MARK_MESSAGED;

/** Reassurance under a messaged badge: messaging is not a claim. */
export const STILL_OPEN_HINT =
  'Still open. You can message them too, and log the reply if they answer you first.';

/**
 * What logging an invite actually earns, phrased from the live rate card.
 *
 * `logBonus` is the total immediate (LOG_BONUS / INFLUENCER_FLAT) amount for
 * the outreacher's creator type. Zero means this team earns nothing at log
 * time and everything downstream, which is a materially different pitch.
 */
export function earningsLine(logBonus: number | null): string {
  if (logBonus === null) return 'What each invite earns is set by your rate card below.';
  if (logBonus > 0) {
    return 'Logging a creator who replied asking to join earns your log bonus, paid in the next payout. Join, post and follower bonuses are added later if that creator joins and posts.';
  }
  return 'Your team earns nothing when you log. The invite starts earning when the creator joins or posts. Logging is what links them to you so those bonuses land on your account.';
}

/**
 * The bar for a reply that counts. This is the single most abused step, so it
 * is stated in terms of concrete examples rather than adjectives.
 */
export const POSITIVE_REPLY = {
  /** Headline. Render this emphasised: bold, or in the warning colour. */
  headline: 'They have to be interested AND asking how to join.',

  /** What qualifies. Both halves are required, not either. */
  qualifies:
    'They said yes and asked how to join: how to sign up, what the next step is, or to send the link.',

  /**
   * What does not. "Asked what it pays" is called out first and deliberately:
   * it's the most common near-miss, it reads as interest, and on its own it
   * is just price shopping.
   */
  doesNot:
    'Asking what the rates are, or what it pays, is NOT enough on its own. Neither is a "no thanks", a read receipt, an emoji reaction, an out-of-office, a "maybe later", or your own unanswered message.',

  /** One-liner for tight spaces (field helper text). */
  short:
    'The screenshot must show them interested and asking how to join. Asking about rates alone does not count.',
} as const;

/**
 * Screenshot rules. The screenshot exists to prove the creator answered AND
 * was interested, so it has to show their message, not the outreacher's.
 */
export const SCREENSHOT_RULE = {
  short: POSITIVE_REPLY.short,

  medium:
    "Upload the conversation showing the creator asking how to join. A screenshot of only your own message doesn't count, nor does a reply turning you down, nor one that only asks what the rates are.",

  formats: 'JPEG, PNG or WebP, up to 5 MB.',

  labelSuffix: (required: boolean): string => (required ? 'Needed to get paid' : 'Recommended'),

  missingError:
    'Your team needs a screenshot of the creator asking how to join before this invite can be paid. Add one to log it.',
} as const;

/**
 * Wording for messaged state on a queue row. Kept here rather than inline so
 * every surface describes the same state identically.
 */
export function contactedSummary(names: string[], opts?: { includesMe?: boolean }): string {
  if (names.length === 0) return '';
  if (opts?.includesMe && names.length === 1) return 'You messaged this creator';
  if (names.length === 1) return `${names[0]} messaged this creator`;
  if (names.length === 2) return `${names[0]} and ${names[1]} messaged this creator`;
  return `${names[0]} and ${names.length - 1} others messaged this creator`;
}
/** Same as {@link contactedSummary}, under the flow's word. */
export const messagedSummary = contactedSummary;

// ── Channels ──────────────────────────────────────────────────────────────

export const CHANNEL_LABELS: Record<InviteChannel, string> = {
  TIKTOK: 'TikTok',
  INSTAGRAM: 'Instagram',
  YOUTUBE: 'YouTube',
  TWITTER: 'X (Twitter)',
  DISCORD: 'Discord',
};
export function channelLabel(c: string | null | undefined): string {
  if (!c) return '';
  return CHANNEL_LABELS[c as InviteChannel] ?? c.charAt(0) + c.slice(1).toLowerCase();
}

/** ["TIKTOK","INSTAGRAM","YOUTUBE"] → "TikTok, Instagram or YouTube". */
export function joinChannels(channels: ReadonlyArray<string>, conjunction = 'or'): string {
  const names = channels.map(channelLabel).filter(Boolean);
  if (names.length === 0) return '';
  if (names.length === 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} ${conjunction} ${names[names.length - 1]}`;
}

/**
 * Normalise what an outreacher typed into the handle the backend will store.
 * Mirrors the backend parser, including its fallback for Discord (no URL
 * shape; bare name with letters, digits, dots and underscores).
 */
const DEFAULT_USERNAME_PATTERN = /^[a-zA-Z0-9._]+$/;
export function normalizeHandle(channel: string, raw: string | null | undefined): SocialHandle | null {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return null;
  if (channel === 'TIKTOK' || channel === 'INSTAGRAM' || channel === 'YOUTUBE' || channel === 'TWITTER') {
    return parseSocialHandle(channel, trimmed);
  }
  if (!channel) return null;
  const stripped = trimmed.replace(/^@+/, '');
  if (!DEFAULT_USERNAME_PATTERN.test(stripped)) return null;
  const username = stripped.toLowerCase();
  return { username, displayHandle: `@${username}`, fromUrl: false };
}

export const INVALID_HANDLE = "That doesn't look like a valid handle or profile link.";

/** Said wherever an outreacher can change an invite's handle. */
export const HANDLE_RECHECK = "We'll check the new handle within a day and update your earnings.";

/** "Already logged by Sam on 29 Sep 2026. Only one invite per creator is paid." */
export function duplicateMessage(match: { inviterName: string | null; createdAt: string }, opts?: { isMe?: boolean }): string {
  const who = opts?.isMe ? 'you' : match.inviterName || 'a teammate';
  return `Already logged by ${who} on ${formatDate(match.createdAt)}. Only one invite per creator is paid.`;
}

// ── Invites ───────────────────────────────────────────────────────────────

/** Where an invite came from. */
export function inviteSourceLabel(discoveryFolderName: string | null | undefined): string {
  return discoveryFolderName ? `Lead queue: ${discoveryFolderName}` : 'Found myself';
}

interface MoneyInput {
  status: InviteStatus | string;
  lockedValue: string | null;
  amountPaid: string | null;
}

function num(v: string | null | undefined): number {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

/** What the invite has earned so far (log, join, post and follower bonuses). */
export function inviteEarned(inv: MoneyInput): number {
  return num(inv.lockedValue);
}

/**
 * Earned minus paid, never negative. Always 0 on a rejected ("Not paid")
 * invite: the reject writes the balance off, but `lockedValue` keeps the stale
 * pre-reject amount, so subtracting would show money that will never come.
 */
export function stillToBePaid(inv: MoneyInput): number {
  if (inv.status === 'REJECTED') return 0;
  return Math.max(0, num(inv.lockedValue) - num(inv.amountPaid));
}

interface StageAgeInput {
  status: InviteStatus | string;
  createdAt: string;
  convertedAt?: string | null;
  releasedAt?: string | null;
  deletedAt?: string | null;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/**
 * How long an invite has sat in its current stage, in words.
 *
 * Only some stages have an entry timestamp: Replied starts at the log,
 * Expired at the release, Posted at the first counted post, Hidden at the
 * delete. Joined and Not paid don't record when they happened, so those fall
 * back to the log date and say so rather than guessing.
 */
export function stageAge(
  inv: StageAgeInput,
  opts: { teamTracks?: boolean; now?: Date } = {},
): { days: number | null; text: string } {
  const now = opts.now ?? new Date();
  const stage = inviteStage(
    { status: inv.status as InviteStatus, deletedAt: inv.deletedAt ?? null, releasedAt: inv.releasedAt ?? null },
    { teamTracks: opts.teamTracks },
  );
  const ago = (d: number): string => (d === 0 ? 'today' : `${plural(d, 'day')} ago`);
  const logged = daysSince(inv.createdAt, now);

  const since = (ts: string | null | undefined, verb: string): { days: number | null; text: string } | null => {
    const d = daysSince(ts, now);
    return d === null ? null : { days: d, text: `${verb} ${ago(d)}` };
  };

  let out: { days: number | null; text: string } | null = null;
  switch (stage.key) {
    case 'replied':
    case 'manual': {
      if (logged !== null) {
        out = { days: logged, text: logged === 0 ? 'Logged today' : `${plural(logged, 'day')} in Replied` };
      }
      break;
    }
    case 'expired':
      out = since(inv.releasedAt, 'Expired');
      break;
    case 'posted':
      out = since(inv.convertedAt, 'Posted');
      break;
    case 'hidden':
      out = since(inv.deletedAt, 'Hidden');
      break;
    default:
      break;
  }
  if (out) return out;
  return logged === null ? { days: null, text: '' } : { days: logged, text: `Logged ${ago(logged)}` };
}

// ── Other statuses, where labels.ts wording differs from the brief ─────────

/**
 * Payout status for outreachers. Same as `payoutStatusLabel`, except
 * SUBMITTED is spelled out in full.
 */
export function payoutStatusText(s: string | null | undefined): string {
  if (s === 'SUBMITTED') return 'Sent to payment provider';
  return payoutStatusLabel(s);
}

/**
 * Lead-queue status. Same as `prospectStatusLabel`, except the legacy
 * CLAIMED state (claims are gone: a lead stays open to everyone until a
 * reply is logged) reads as Open.
 */
export function leadStatusLabel(s: string | null | undefined): string {
  if (s === 'CLAIMED') return prospectStatusLabel('AVAILABLE');
  return prospectStatusLabel(s);
}

// ── My creators (invites page) tabs ───────────────────────────────────────

export type MyCreatorsTab = 'all' | 'messaged' | 'replied' | 'joined' | 'posted' | 'notpaid';

export const MY_CREATORS_TABS: ReadonlyArray<{ key: MyCreatorsTab; label: string; status?: string }> = [
  { key: 'all', label: 'All' },
  { key: 'messaged', label: 'Messaged', status: 'CONTACTED' },
  { key: 'replied', label: 'Replied', status: 'PENDING' },
  { key: 'joined', label: 'Joined', status: 'VERIFIED' },
  { key: 'posted', label: 'Posted', status: 'CONVERTED' },
  { key: 'notpaid', label: 'Not paid', status: 'REJECTED' },
];

/** Old `?tab=` values, so existing links and bookmarks still land on the right tab. */
const LEGACY_TABS: Record<string, MyCreatorsTab> = {
  contacted: 'messaged',
  pending: 'replied',
  verified: 'joined',
  converted: 'posted',
  rejected: 'notpaid',
};

export function resolveMyCreatorsTab(raw: string | null | undefined): MyCreatorsTab {
  const v = (raw ?? '').toLowerCase();
  if (MY_CREATORS_TABS.some((t) => t.key === v)) return v as MyCreatorsTab;
  return LEGACY_TABS[v] ?? 'all';
}
